import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Globe, AlertTriangle, Upload, ExternalLink, Image as ImageIcon } from 'lucide-react';
import API_URL from '../../config/api';
import CustomAlert from './shared/CustomAlert';

// ⭐ Panel de personalización del Menú Web Estático (plan Premium+). v1
// deliberadamente NO es un editor libre: el dueño elige una de las
// plantillas ya diseñadas (ver MenuPublico.js) y solo ajusta logo, colores,
// portada y tagline -- decisión tomada explícitamente para no construir un
// constructor de sitios completo en la primera versión.

// Mismo patrón de compresión de imágenes que ya usa ProductosManager.js
// (base64 directo, sin servicio de almacenamiento externo) -- se duplica
// acá en vez de importar desde allá para no crear un acoplamiento entre
// dos pantallas que no tienen otra relación.
const compressImage = (file, maxWidth, quality) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new window.Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', quality));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const TEMPLATE_OPTIONS = [
  { id: 'elegante', label: 'Elegante', desc: 'Fondo oscuro, acentos dorados, tipografía clásica de restaurante fino.', swatch: ['#111827', '#f59e0b'] },
  { id: 'casual', label: 'Casual', desc: 'Colorido, tarjetas grandes con foto, ideal para ambiente relajado.', swatch: ['#2563eb', '#f97316'] },
  { id: 'minimalista', label: 'Minimalista', desc: 'Mucho blanco, tipografía fina, sin ruido visual.', swatch: ['#111827', '#059669'] },
];

const EMPTY_FORM = {
  menu_subdomain: '',
  menu_enabled: false,
  menu_template: 'elegante',
  menu_logo_url: '',
  menu_cover_url: '',
  menu_primary_color: '#1e3a8a',
  menu_secondary_color: '#f59e0b',
  menu_tagline: ''
};

// Mismo criterio de "días vs horas" que usa el contador de Alertas: mientras
// falte más de un día se muestra en días (3, 2, 1); en el último día se
// muestra en horas (24h, 23h, ...) para transmitir más urgencia.
function formatTrialCountdown(endsAtValue) {
  if (!endsAtValue) return null;
  const end = new Date(String(endsAtValue).replace(' ', 'T') + 'Z').getTime();
  const msLeft = end - Date.now();
  if (msLeft <= 0) return 'Prueba vencida';
  const totalHours = Math.ceil(msLeft / 3600000);
  if (totalHours > 24) {
    const days = Math.ceil(totalHours / 24);
    return `${days} día${days === 1 ? '' : 's'} restantes`;
  }
  return `${totalHours}h restantes`;
}

export default function MenuWebManager({ token }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [canUseMenu, setCanUseMenu] = useState(true);
  const [menuTrialActive, setMenuTrialActive] = useState(false);
  const [menuTrialEndsAt, setMenuTrialEndsAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [customAlert, setCustomAlert] = useState(null);
  const logoInputRef = useRef(null);
  const coverInputRef = useRef(null);

  const showAlert = (type, title, message) => setCustomAlert({ type, title, message });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/pos/menu-config`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCanUseMenu(res.data.canUseMenu);
        setMenuTrialActive(!!res.data.menuTrialActive);
        setMenuTrialEndsAt(res.data.menu_trial_ends_at || null);
        setForm({
          menu_subdomain: res.data.menu_subdomain || '',
          menu_enabled: !!res.data.menu_enabled,
          menu_template: res.data.menu_template || 'elegante',
          menu_logo_url: res.data.menu_logo_url || '',
          menu_cover_url: res.data.menu_cover_url || '',
          menu_primary_color: res.data.menu_primary_color || '#1e3a8a',
          menu_secondary_color: res.data.menu_secondary_color || '#f59e0b',
          menu_tagline: res.data.menu_tagline || ''
        });
      } catch (err) {
        showAlert('error', 'Error', 'No se pudo cargar la configuración del menú web.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const handleImagePick = async (e, field, maxWidth, setUploading) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showAlert('warning', 'Archivo inválido', 'Selecciona una imagen (JPG, PNG, WEBP, etc.)');
      return;
    }
    if (file.size > 6 * 1024 * 1024) {
      showAlert('warning', 'Imagen muy grande', 'La imagen no debe superar los 6MB.');
      return;
    }
    setUploading(true);
    try {
      const base64 = await compressImage(file, maxWidth, 0.8);
      setForm((prev) => ({ ...prev, [field]: base64 }));
    } catch (err) {
      showAlert('error', 'Error', 'No se pudo procesar la imagen.');
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    const slug = form.menu_subdomain.trim().toLowerCase();
    if (!slug) {
      showAlert('warning', 'Falta el subdominio', 'Elige un identificador único para tu menú (ej. "la-sazon-de-oso").');
      return;
    }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/pos/menu-config`, { ...form, menu_subdomain: slug }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setForm((prev) => ({ ...prev, menu_subdomain: slug }));
      showAlert('success', 'Guardado', 'Tu menú web se actualizó correctamente.');
    } catch (err) {
      const backendMsg = err.response?.data?.error;
      showAlert('error', 'No se pudo guardar', backendMsg || 'Intenta nuevamente.');
    } finally {
      setSaving(false);
    }
  };

  const publicUrl = form.menu_subdomain ? `${window.location.origin}/menu/${form.menu_subdomain}` : '';

  if (loading) {
    return <div className="text-center py-16 text-gray-400">Cargando...</div>;
  }

  if (!canUseMenu) {
    return (
      <div className="max-w-lg mx-auto text-center py-16">
        <Globe className="w-14 h-14 text-gray-300 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-800 mb-2">Menú Web Estático</h2>
        <p className="text-gray-500 text-sm">Esta función está disponible desde el plan Premium. Actualiza tu plan para crear el menú digital de tu negocio.</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto pb-10">
      {customAlert && <CustomAlert {...customAlert} onClose={() => setCustomAlert(null)} compact zIndex={70} />}

      {menuTrialActive && (
        <div className="mb-4 flex items-center gap-3 bg-amber-50 border border-amber-300 text-amber-800 rounded-lg px-4 py-3 text-sm">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span>
            Estás probando el Menú Web Estático gratis. <strong>{formatTrialCountdown(menuTrialEndsAt)}</strong> — actualiza a plan Premium antes de que termine para no perder el acceso.
          </span>
        </div>
      )}

      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
            <Globe className="w-5 h-5 text-blue-600" /> Menú Web Estático
          </h2>
          <p className="text-sm text-gray-500">Así verán tu carta los comensales al escanear el QR de la mesa.</p>
        </div>
        <label className="flex items-center gap-2 bg-white border border-gray-200 rounded-lg px-3 py-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.menu_enabled}
            onChange={(e) => setForm((prev) => ({ ...prev, menu_enabled: e.target.checked }))}
            className="w-4 h-4"
          />
          <span className="text-sm font-semibold text-gray-700">{form.menu_enabled ? 'Activo' : 'Inactivo'}</span>
        </label>
      </div>

      {/* Subdominio */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <label className="block text-sm font-semibold text-gray-700 mb-1">Dirección de tu menú</label>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-400 whitespace-nowrap">{window.location.origin}/menu/</span>
          <input
            type="text"
            value={form.menu_subdomain}
            onChange={(e) => setForm((prev) => ({ ...prev, menu_subdomain: e.target.value.toLowerCase() }))}
            placeholder="la-sazon-de-oso"
            className="flex-1 px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <p className="text-xs text-gray-400 mt-1">Solo minúsculas, números y guiones. Es lo que verán tus clientes en el enlace.</p>
        {publicUrl && (
          <a href={publicUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-2">
            <ExternalLink className="w-3 h-3" /> Ver mi menú
          </a>
        )}
      </div>

      {/* Plantilla */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <label className="block text-sm font-semibold text-gray-700 mb-3">Estilo visual</label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {TEMPLATE_OPTIONS.map((t) => (
            <button
              key={t.id}
              onClick={() => setForm((prev) => ({ ...prev, menu_template: t.id }))}
              className={`text-left p-3 rounded-xl border-2 transition-all ${form.menu_template === t.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}
            >
              <div className="flex gap-1 mb-2">
                <span className="w-6 h-6 rounded-full" style={{ backgroundColor: t.swatch[0] }} />
                <span className="w-6 h-6 rounded-full" style={{ backgroundColor: t.swatch[1] }} />
              </div>
              <p className="font-semibold text-sm text-gray-800">{t.label}</p>
              <p className="text-xs text-gray-500 mt-0.5">{t.desc}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Logo y portada */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Logo</label>
          <div
            onClick={() => logoInputRef.current?.click()}
            className={`flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${uploadingLogo ? 'border-blue-300 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'}`}
          >
            {form.menu_logo_url ? (
              <img src={form.menu_logo_url} alt="Logo" className="h-24 object-contain" />
            ) : (
              <>
                <Upload className="w-6 h-6 text-gray-400 mb-1" />
                <p className="text-xs text-gray-400">{uploadingLogo ? 'Procesando...' : 'Subir logo'}</p>
              </>
            )}
          </div>
          <input ref={logoInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleImagePick(e, 'menu_logo_url', 300, setUploadingLogo)} />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Foto de portada</label>
          <div
            onClick={() => coverInputRef.current?.click()}
            className={`flex flex-col items-center justify-center w-full h-28 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${uploadingCover ? 'border-blue-300 bg-blue-50' : 'border-gray-300 bg-gray-50 hover:bg-gray-100'}`}
          >
            {form.menu_cover_url ? (
              <img src={form.menu_cover_url} alt="Portada" className="h-24 w-full object-cover rounded" />
            ) : (
              <>
                <ImageIcon className="w-6 h-6 text-gray-400 mb-1" />
                <p className="text-xs text-gray-400">{uploadingCover ? 'Procesando...' : 'Subir foto de portada'}</p>
              </>
            )}
          </div>
          <input ref={coverInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleImagePick(e, 'menu_cover_url', 1200, setUploadingCover)} />
        </div>
      </div>

      {/* Colores y tagline */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Color primario</label>
            <input
              type="color"
              value={form.menu_primary_color}
              onChange={(e) => setForm((prev) => ({ ...prev, menu_primary_color: e.target.value }))}
              className="w-full h-10 rounded-lg border cursor-pointer"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Color secundario</label>
            <input
              type="color"
              value={form.menu_secondary_color}
              onChange={(e) => setForm((prev) => ({ ...prev, menu_secondary_color: e.target.value }))}
              className="w-full h-10 rounded-lg border cursor-pointer"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Frase corta (opcional)</label>
          <input
            type="text"
            value={form.menu_tagline}
            onChange={(e) => setForm((prev) => ({ ...prev, menu_tagline: e.target.value.slice(0, 200) }))}
            placeholder="Ej. Sabor casero desde 1998"
            className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      <button
        onClick={handleSave}
        disabled={saving || uploadingLogo || uploadingCover}
        className="w-full bg-blue-600 text-white py-3 rounded-lg font-bold hover:bg-blue-700 disabled:opacity-60"
      >
        {saving ? 'Guardando...' : 'Guardar cambios'}
      </button>
    </div>
  );
}
